<template>
    <ak-form-decorator
        :id="id"
        :title="title"
        :helpText="helpText"
        :error="error"
        :hasError="hasError"
        :required="required"
        :value="value"
        :hidden="hidden"
        :errorLocales="errorLocales"
    >
        <div class="ak-relation" :data-relation-id="id">
            <draggable
                v-if="currentRelation.selected.length"
                v-model="currentRelation.selected"
                :disabled="!currentRelation.enableSorting"
                class="ak-relation__selected"
            >
                <transition-group type="transition" name="flip-list" tag="div">
                    <div
                        v-for="(item, key) in selectedItems"
                        :key="item.id"
                        class="ak-relation__selected-item"
                    >
                        <div
                            v-if="currentRelation.enableSorting"
                            class="ak-relation__selected-item-drag-handle"
                        >
                            <ak-icon-button>drag_handle</ak-icon-button>
                        </div>

                        <div
                            class="ak-relation__selected-values"
                            :class="{'ak-relation__selected-values--first': !currentRelation.enableSorting}"
                        >
                            <div class="ak-relation__selected-prop">
                                <ak-widget
                                    v-if="item.widget"
                                    :type="item.widget.type"
                                    :attributes="{...item.widget.attributes, id: item.widget.id}"
                                    :callbacks="item.widget.callbacks"
                                    :slot="item.widget.slot"
                                    :objectId="item.id"
                                    contextType="grid-cell"
                                    :callbackUri="`/${currentRelation.foreignList.bundle}/${currentRelation.foreignList.id}/`"
                                >
                                    <template v-for="slot in item.widget.slot" :slot="slot.type">
                                        {{ slot.value }}
                                    </template>
                                </ak-widget>
                                <span v-else>{{ item.entry }}</span>
                            </div>

                            <div
                                v-for="(widget, widgetKey) in item.widgets.addOns"
                                :key="widget.id + '-' + key"
                                class="ak-relation__selected-prop"
                            >
                                <ak-widget
                                    :type="widget.type"
                                    :attributes="addOnWidgetAttributes(widget, widget.id, item)"
                                    :callbacks="widget.callbacks"
                                    :slot="widget.slot"
                                    :objectId="item.id"
                                    contextType="grid-cell"
                                    :callbackUri="`/${currentRelation.foreignList.bundle}/${currentRelation.foreignList.id}/`"
                                    @input="handleWidgetInput(widget.id, item, $event)"
                                    @blur="validateAddOnValue(widget.id, item.addOnValues, item)"
                                >
                                    {{ item.addOnValues[widgetKey] }}
                                </ak-widget>
                            </div>

                            <div v-if="!enableForeignList" class="ak-relation__selected-item-edit">
                                <ak-icon-button :href="editSelectedUrl(item.id)">edit</ak-icon-button>
                            </div>
                        </div>

                        <div class="ak-relation__selected-item-delete">
                            <ak-icon-button
                                @click="enableForeignList ? deleteSelected(item.id) : openDeleteDialog(item.id)"
                            >
                                delete
                            </ak-icon-button>
                        </div>
                    </div>
                </transition-group>
            </draggable>

            <ak-button
                class="ak-relation__select-button"
                :outlined="true"
                size="small"
                @click="$router.push(relationUrl())"
            >
                <span v-if="!currentRelation.selected.length && enableForeignList" v-translate>Selecteren</span>
                <span v-else-if="!enableForeignList" v-translate>Toevoegen</span>
                <span v-else v-translate>Wijzigen</span>
            </ak-button>

            <ak-stock-relation-resolver
                v-if="renderRelationResolver"
                :relationString="currentRelationString"
                :relationReference="relationReference"
                :defaultIdsSelected="selectedIds"
                :enableForeignList="enableForeignList"
                :filterOrderId="filterOrderId"
                @close="triggerClose"
                @updateRelations="updateRelations"
                @refresh="updateRelations"
            />
        </div>

        <ak-modal
            v-model="deleteDialog"
            max-width="350"
            :title="$t('Bent u zeker dat u dit item wilt verwijderen?')"
            :confirmText="$t('Verwijderen')"
            :cancelText="$t('Annuleren')"
            @onCancel="deleteDialog = false; deleteDialogItemId = null"
            @onConfirm="deleteSelected(deleteDialogItemId); deleteDialog = false; deleteDialogItemId = null"
        />
    </ak-form-decorator>
</template>

<script>
import FormElementRelationMixin from '../../../../vendor/multimedium/spine/resources/spine-appkit-vue/src/components/form/mixins/FormElementRelationMixin.js';
import AkStockRelationResolver from './AkStockRelationResolver.js';

export default {
    name: 'ak-stock-product-relation',
    mixins: [FormElementRelationMixin],
    components: {
        AkStockRelationResolver,
    },
    props: {
        enableForeignList: {
            type: Boolean,
            default: true,
        },
        baseUri: {
            type: String,
            required: false,
            default: '',
        },
        filterOrderId: {
            type: String,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            deleteDialog: false,
            deleteDialogItemId: null,
        };
    },
    computed: {
        relationReference() {
            return `${this.bundleId},${this.definitionId}`;
        },
    },
    watch: {
        currentRelation: {
            deep: true,
            handler() {
                this.setCurrentValue(this.currentRelation.selected);
            },
        },
        value(newValue, oldValue) {
            if (!this.$deepEqual(newValue, oldValue)) {
                this.currentRelation.selected = newValue;
            }
        },
    },
    methods: {
        relationUrl() {
            const params = {...this.$route.params};
            let relationAddon = `${this.id}:grid:${this.currentRelation.foreignList.bundle}:${this.currentRelation.foreignList.id}`;

            if (!this.enableForeignList) {
                relationAddon = `${this.id}:form:${this.currentRelation.foreignList.bundle}:form:new`;
            }

            params.relationString = params.relationString
                ? `${params.relationString}:${relationAddon}`
                : relationAddon;

            return {
                name: this.$route.name,
                params,
                query: this.$route.query,
            };
        },
        editSelectedUrl(selectedItemId) {
            const to = this.relationUrl();
            let relationString = to.params.relationString;
            relationString = relationString.substr(0, relationString.indexOf(':new'));
            to.params.relationString = `${relationString}:${selectedItemId}`;
            return this.$router.resolve(to).href;
        },
        addOnWidgetAttributes(widget, widgetKey, item) {
            const output = {...widget.formControl, ...widget.attributes, id: widget.id, baseUri: this.baseUri};
            if (!output.value && item.addOnValues[widgetKey]) {
                output.value = item.addOnValues[widgetKey];
            }
            return output;
        },
        openDeleteDialog(id) {
            this.deleteDialog = true;
            this.deleteDialogItemId = id;
        },
        handleWidgetInput(widgetKey, item, value) {
            if (!value) return;

            const currentSelection = this.currentRelation.selected.find(selectedItem => selectedItem.id === item.id);
            const addOnValues = {...currentSelection.addOnValues};
            addOnValues[widgetKey] = value;
            currentSelection.addOnValues = addOnValues;
        },
        async validateAddOnValue(id, values, item) {
            if (!id) return;

            const path = `/${this.bundleId}/${this.definitionId}/validate-add-on`;
            const result = await this.$post(path, {
                addOnId: id,
                addOnValues: {...values},
            });
            const selectedItem = this.currentRelation.selected.find(selected => selected.id === item.id);
            const widget = selectedItem.widgets.addOns.find(addOn => addOn.id === id);
            widget.formControl = {...widget.formControl, ...result[id]};
        },
        setCurrentValue(selected) {
            this.currentValue = selected.map(item => {
                const output = {
                    id: item.id,
                    addOnValues: item.addOnValues,
                };
                if (item.entry) output.entry = item.entry;
                if (item.widget) output.widget = item.widget;
                return output;
            });
        },
        formatStateOutput(value) {
            if (!value) return value;
            if (!this.relation.enableMultiple) {
                return Array.isArray(value) && value[0] ? value[0].id : value;
            }
            return value.map(item => item.id);
        },
        triggerClose() {
            this.$router.back();
        },
    },
};
</script>
